import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiService } from '@services/api.service';
import { LoggerService } from '@services/logger.service';
import {
  RentalTimelineGroup,
  RentalTimelineItem,
  TimelineApiGroup,
  TimelineApiItem,
  TimelineApiResponse,
  TimelineFilters,
} from '@/models/rentals/rental-timeline.model';

export interface RentalTimelineData {
  groups: RentalTimelineGroup[];
  items: RentalTimelineItem[];
}

@Injectable({
  providedIn: 'root',
})
export class RentalTimelineService extends ApiService {
  getTimeline(filters: TimelineFilters): Observable<RentalTimelineData> {
    const params = new HttpParams()
      .set('dataInizio', filters.dataInizio)
      .set('dataFine', filters.dataFine)
      .set('marcaId', String(filters.marcaId));

    return this.http
      .get<TimelineApiResponse>(this.baseUrl + '/rental-practices/timeline', { params })
      .pipe(
        map((response) => {
          LoggerService.log(response, 'getTimeline');
          return {
            groups: this.mapGroups(response?.groups ?? []),
            items: this.mapItems(response?.items ?? []),
          };
        }),
      );
  }

  getBrands(): Observable<any> {
    return this.http.get(this.baseUrl + `/rental-practices/timeline/brands`).pipe(
      map((response: any) => {
        LoggerService.log(response, 'getBrands');
        return response;
      }),
    );
  }

  mapGroups(apiGroups: TimelineApiGroup[]): RentalTimelineGroup[] {
    return apiGroups.map((g) => ({
      id: g.gruppoId ?? g.id,
      gruppoId: g.gruppoId,
      content: this.buildGroupContent(g),
      targa: g.targa ?? '',
      marca: g.descrizioneMarca ?? '',
      modello: g.modello ?? '',
      flotta: !!g.flotta,
      marcaId: g.marcaId,
      tipo: g.tipo,
      dettaglio: g.dettaglio,
      tooltipHtml: this.buildGroupTooltip(g),
    }));
  }

  mapItems(apiItems: TimelineApiItem[]): RentalTimelineItem[] {
    return apiItems
      .map((i) => {
        const start = i.dataInizio ? new Date(i.dataInizio) : null;
        const end = i.dataFine ? new Date(i.dataFine) : null;
        if (!start || !end || isNaN(start.getTime()) || isNaN(end.getTime())) {
          return null;
        }

        const startDay = new Date(start.getFullYear(), start.getMonth(), start.getDate()).getTime();
        const endDay = new Date(end.getFullYear(), end.getMonth(), end.getDate()).getTime();
        const durataGiorni = Math.max(1, Math.floor((endDay - startDay) / 86400000) + 1);

        return {
          id: i.id,
          itemId: i.itemId,
          group: i.gruppoId,
          content: i.cliente || 'N/D',
          start,
          end,
          className: this.resolveClassName(i.tipo),
          stato: i.tipo ?? '',
          durataGiorni,
          practiceId: i.gruppoId,
          dettaglioId: i.dettaglioId,
          tipo: i.tipo,
          title: this.buildItemTooltip(i, start, end, durataGiorni),
        } as RentalTimelineItem;
      })
      .filter((item): item is RentalTimelineItem => item !== null);
  }

  private buildGroupContent(g: TimelineApiGroup): string {
    return g.dettaglio || '---';
  }

  private buildGroupTooltip(g: TimelineApiGroup): string {
    const targa = this.escapeHtml(g.targa ?? '-');
    const marca = this.escapeHtml(g.descrizioneMarca ?? '-');
    const modelloRaw = g.tipo === 'PRENOTAZIONE_LIBERA' ? g.dettaglio ?? '-' : g.modello ?? '-';
    const modello = this.escapeHtml(modelloRaw || '-');

    return (
      `<div class="vis-tooltip-content">` +
      `<div class="vis-tooltip-title">${targa}</div>` +
      `<div class="vis-tooltip-row"><span class="vis-tooltip-label">Marca</span><span class="vis-tooltip-value">${marca}</span></div>` +
      `<div class="vis-tooltip-row"><span class="vis-tooltip-label">Modello</span><span class="vis-tooltip-value">${modello}</span></div>` +
      `</div>`
    );
  }

  private buildItemTooltip(
    i: TimelineApiItem,
    start: Date,
    end: Date,
    durataGiorni: number,
  ): string {
    const cliente = this.escapeHtml(i.cliente || 'N/D');
    const tipoLabel = this.resolveTipoLabel(i.tipo);
    const tipoClass = this.resolveClassName(i.tipo);
    const periodo = `${this.formatDate(start)} → ${this.formatDate(end)}`;
    const durata = `${durataGiorni} ${durataGiorni === 1 ? 'giorno' : 'giorni'}`;

    return (
      `<div class="vis-tooltip-content">` +
      `<div class="vis-tooltip-title">${cliente}</div>` +
      `<div class="vis-tooltip-row"><span class="vis-tooltip-label">Periodo</span><span class="vis-tooltip-value">${periodo}</span></div>` +
      `<div class="vis-tooltip-row"><span class="vis-tooltip-label">Durata</span><span class="vis-tooltip-value">${durata}</span></div>` +
      `<div class="vis-tooltip-row"><span class="vis-tooltip-label">Tipo</span><span class="vis-tooltip-chip vis-tooltip-chip--${tipoClass}">${tipoLabel}</span></div>` +
      `</div>`
    );
  }

  private resolveTipoLabel(tipo?: string): string {
    switch (tipo) {
      case 'NOLEGGIO_PRATICA':
        return 'Noleggio';
      case 'PRENOTAZIONE_PRATICA':
        return 'Prenotazione';
      case 'PRENOTAZIONE_LIBERA':
        return 'Prenotazione libera';
      default:
        return tipo ?? '-';
    }
  }

  private formatDate(d: Date): string {
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  }

  private escapeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  private resolveClassName(tipo?: string): string {
    switch (tipo) {
      case 'NOLEGGIO_PRATICA':
        return 'rental-noleggio';
      case 'PRENOTAZIONE_PRATICA':
        return 'rental-prenotazione';
      case 'PRENOTAZIONE_LIBERA':
        return 'rental-prenotazione-libera';
      default:
        return 'rental-noleggio';
    }
  }
}
